import {
  CONTENT_TYPES,
  VOICE_CALL_STATUS,
} from 'dashboard/components-next/message/constants';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { useCallsStore } from 'dashboard/stores/calls';
import types from 'dashboard/store/mutation-types';
import { hasPushPermissions } from 'dashboard/helper/pushHelper';
import i18n from 'dashboard/i18n/instance';

export const TERMINAL_STATUSES = [
  'completed',
  'busy',
  'failed',
  'rejected',
  'no-answer',
  'canceled',
  'missed',
  'ended',
];

// A message.created for a ringing call is queued through ActionCableBroadcastJob and can
// be delivered after the call has already been accepted/ended via a synchronous broadcast.
// Track dismissed call sids at module scope so that late, stale "ringing" snapshot doesn't
// resurrect a card every caller of handleVoiceCallCreated (hydration and real-time alike)
// has already cleared.
const dismissedCallSids = new Set();
export const markCallDismissed = callSid => {
  if (callSid) dismissedCallSids.add(callSid);
};

// Set before the accept/join API so a synchronous voice_call.accepted (or a
// message.updated that lands mid-join) does not dismiss this tab's ringing card
// before setCallActive runs.
const acceptingCallSids = new Set();
export const markCallAccepting = callSid => {
  if (callSid) acceptingCallSids.add(callSid);
};
export const clearCallAccepting = callSid => {
  acceptingCallSids.delete(callSid);
};
export const isCallAccepting = callSid => acceptingCallSids.has(callSid);

export const isInbound = direction => direction === 'inbound';

export const callProviderCallId = call =>
  call?.providerCallId || call?.provider_call_id || null;

export const callDirectionFrom = call => {
  const direction = call?.direction;
  if (direction === 'outgoing' || direction === 'outbound') return 'outbound';
  return 'inbound';
};

export function notifyIncomingVoiceCall({ callerName, callSid } = {}) {
  if (typeof document === 'undefined' || !document.hidden) return;
  if (!hasPushPermissions()) return;

  const title = i18n.global.t('CONVERSATION.HEADER.INCOMING_CALL');
  const body = callerName
    ? i18n.global.t('CONVERSATION.HEADER.INCOMING_CALL_FROM', {
        name: callerName,
      })
    : '';

  try {
    const notification = new Notification(title, {
      body,
      tag: `incoming-voice-call-${callSid || 'unknown'}`,
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch (_) {
    /* noop — Notification can throw if the document is not fully active */
  }
}

const isVoiceCallMessage = message => {
  return CONTENT_TYPES.VOICE_CALL === message?.content_type;
};

const shouldSkipCall = (callDirection, senderId, currentUserId) => {
  return callDirection === 'outbound' && senderId !== currentUserId;
};

const shouldShowCall = ({ callDirection, senderId, currentUserId }) => {
  // Inbound ringing is for inbox members (cable + online), not the
  // conversation assignee — another member may accept an already-assigned thread.
  // Outbound stays scoped to the initiator via shouldSkipCall, even when
  // auto-assignment points the conversation at someone else.
  return !shouldSkipCall(callDirection, senderId, currentUserId);
};

// Offline/busy agents shouldn't get a ringing popup for inbound calls, but
// outbound calls always belong to the initiator regardless of their status,
// and existing (already-surfaced) calls keep going so a status change
// mid-call doesn't yank away an active widget.
const shouldRingInbound = (callDirection, currentUserAvailability) => {
  if (callDirection === 'outbound') return true;
  return currentUserAvailability === 'online';
};

function extractCallerSnapshot(message) {
  // Snapshot caller info from the message at add-time so the widget can keep
  // rendering it after the user navigates away from a conversation list that
  // had the conversation hydrated (and Vuex evicts it from the store).
  // Only incoming messages carry the contact as the sender; on outbound calls
  // the sender is the initiating agent, so skip the snapshot and let the widget
  // fall back to the conversation's contact (conversation.meta.sender).
  if (message?.message_type !== MESSAGE_TYPE.INCOMING) return null;
  const sender = message?.sender;
  if (!sender) return null;
  return {
    name: sender.name,
    phone: sender.phone_number,
    avatar: sender.avatar || sender.thumbnail,
    additionalAttributes: sender.additional_attributes || {},
  };
}

function extractCallData(message) {
  const call = message?.call || {};
  return {
    callSid: callProviderCallId(call),
    callId: call.id,
    provider: call.provider,
    status: call.status,
    callDirection: callDirectionFrom(call),
    conversationId: message?.conversation_id,
    inboxId: message?.inbox_id ?? message?.conversation?.inbox_id,
    senderId: message?.sender?.id,
    caller: extractCallerSnapshot(message),
  };
}

export function handleVoiceCallCreated(
  message,
  currentUserId,
  currentUserAvailability
) {
  if (!isVoiceCallMessage(message)) return;

  const {
    callSid,
    callId,
    provider,
    status,
    callDirection,
    conversationId,
    inboxId,
    senderId,
  } = extractCallData(message);

  if (callSid && dismissedCallSids.has(callSid)) return;

  // A voice_call message can be created already terminal when the caller hangs
  // up before connect. Only ring while the call is actually ringing; mirrors the
  // guard in seedCallsFromHydratedMessages.
  if (status !== VOICE_CALL_STATUS.RINGING) return;

  if (!shouldShowCall({ callDirection, senderId, currentUserId })) {
    return;
  }

  if (!shouldRingInbound(callDirection, currentUserAvailability)) return;

  const callsStore = useCallsStore();
  const caller = extractCallerSnapshot(message);
  callsStore.addCall({
    callSid,
    callId,
    provider,
    conversationId,
    inboxId,
    callDirection,
    senderId,
    caller,
  });

  if (callDirection === 'inbound') {
    notifyIncomingVoiceCall({ callerName: caller?.name, callSid });
  }
}

function dismissRingingIfTaken(callsStore, callSid) {
  markCallDismissed(callSid);
  const existing = callsStore.calls.find(call => call.callSid === callSid);
  if (existing?.isActive || isCallAccepting(callSid)) return;

  callsStore.dismissCall(callSid);
}

export function handleVoiceCallUpdated(
  commit,
  message,
  currentUserId,
  currentUserAvailability
) {
  if (!isVoiceCallMessage(message)) return;

  const {
    callSid,
    callId,
    provider,
    status,
    callDirection,
    conversationId,
    inboxId,
    senderId,
  } = extractCallData(message);

  const callsStore = useCallsStore();

  // Guard against a still-queued ringing message.created arriving after this
  // terminal update, same as the accepted/ended broadcast handlers.
  if (TERMINAL_STATUSES.includes(status)) markCallDismissed(callSid);

  callsStore.handleCallStatusChanged({ callSid, status, conversationId });

  commit(types.UPDATE_MESSAGE_CALL_STATUS, {
    conversationId,
    callStatus: status,
    callSid,
  });

  if (!shouldShowCall({ callDirection, senderId, currentUserId })) {
    callsStore.removeCall(callSid);
    return;
  }

  if (status === VOICE_CALL_STATUS.IN_PROGRESS && callDirection === 'inbound') {
    dismissRingingIfTaken(callsStore, callSid);
    return;
  }

  if (status === VOICE_CALL_STATUS.RINGING) {
    if (!shouldRingInbound(callDirection, currentUserAvailability)) return;

    callsStore.addCall({
      callSid,
      callId,
      provider,
      conversationId,
      inboxId,
      callDirection,
      senderId,
      caller: extractCallerSnapshot(message),
    });
  }
}

export function syncConversationCallVisibility(conversation, currentUserId) {
  // Inbound widgets ignore conversation assignee. Only drop outbound cards
  // that never belonged to this user (e.g. another agent's outbound message).
  const callsStore = useCallsStore();
  const callsToRemove = callsStore.calls.filter(
    call =>
      call.conversationId === conversation.id &&
      !shouldShowCall({
        callDirection: call.callDirection,
        senderId: call.senderId,
        currentUserId,
      })
  );
  callsToRemove.forEach(call => callsStore.removeCall(call.callSid));
}
